import React, { useEffect, useRef, useState } from 'react';
import { defaultVideoLoadingLogoAsset } from '../../lib/siteAssets';

function inferVideoType(src) {
  if (!src) {
    return 'video/mp4';
  }

  if (src.endsWith('.mov')) {
    return 'video/quicktime';
  }

  if (src.endsWith('.webm')) {
    return 'video/webm';
  }

  return 'video/mp4';
}

export function LazyVideo({
  src,
  poster,
  className = '',
  videoClassName = '',
  caption,
  children,
  showLoadingOverlay = false,
  loadingLogoSrc = defaultVideoLoadingLogoAsset,
  eager = false,
}) {
  const [shouldLoad, setShouldLoad] = useState(eager);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const isReady = loadedSrc === src;
  const resolvedPoster = showLoadingOverlay ? undefined : poster;

  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '240px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad || !videoRef.current) {
      return undefined;
    }

    const videoNode = videoRef.current;

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[LazyVideo] loading source', {
        src,
        poster,
      });
    }

    videoNode.pause();
    videoNode.load();

    const playPromise = videoNode.play();

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[LazyVideo] autoplay blocked', {
            src,
            error: error?.message || error,
          });
        }
      });
    }

    return undefined;
  }, [poster, shouldLoad, src]);

  return (
    <div className={className} ref={containerRef}>
      <video
        ref={videoRef}
        className={`${videoClassName} ${isReady ? 'is-ready' : 'is-loading'}`.trim()}
        autoPlay
        muted
        loop
        playsInline
        preload={shouldLoad ? 'metadata' : 'none'}
        poster={resolvedPoster}
        onLoadedData={() => {
          setLoadedSrc(src);
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[LazyVideo] loaded data', { src });
          }
        }}
      >
        {shouldLoad ? <source src={src} type={inferVideoType(src)} /> : null}
      </video>
      {showLoadingOverlay && !isReady ? (
        <div className='video-loading-overlay' aria-hidden='true'>
          <div className='video-loading-backdrop' />
          <img className='video-loading-logo' src={loadingLogoSrc} alt='' />
        </div>
      ) : null}
      {caption ? <p className='media-caption'>{caption}</p> : null}
      {children}
    </div>
  );
}
